import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import API_URL from '../config';
import InvitationHandler from './InvitationHandler';
import Mixpanel from '../utils/mixpanel';
import logger from '../utils/logger';

// Sports and positions data
const SPORTS_POSITIONS_DATA = {
  'Badminton': ['Singles', 'Doubles', 'Mixed Doubles'],
  'Baseball': ['Pitcher', 'Catcher', 'First Base', 'Second Base', 'Third Base', 'Shortstop', 'Left Field', 'Center Field', 'Right Field', 'Designated Hitter', 'Utility Player'],
  'Basketball': ['Point Guard', 'Shooting Guard', 'Small Forward', 'Power Forward', 'Center'],
  'Bowling': ['Individual Bowler', 'Team Bowler'],
  'Cheerleading': ['Flyer', 'Base', 'Back Spot', 'Tumbler', 'Stunter'],
  'Cross Country': ['Runner'],
  'Dance': ['Pom', 'Jazz', 'Hip-Hop', 'Kick', 'Contemporary'],
  'Field Hockey': ['Goalkeeper', 'Defender', 'Midfielder', 'Forward'],
  'Flag Football': ['Quarterback', 'Running Back', 'Wide Receiver', 'Center', 'Linebacker', 'Cornerback', 'Safety', 'Rusher'],
  'Football': ['Quarterback', 'Running Back', 'Fullback', 'Wide Receiver', 'Tight End', 'Offensive Tackle', 'Offensive Guard', 'Center', 'Defensive End', 'Defensive Tackle', 'Linebacker', 'Cornerback', 'Safety', 'Kicker', 'Punter', 'Long Snapper', 'Kick Returner', 'Punt Returner'],
  'Golf': ['Individual Golfer', 'Team Golfer'],
  'Gymnastics': ['Vault', 'Uneven Bars', 'Balance Beam', 'Floor Exercise', 'All-Around', 'Pommel Horse', 'Still Rings', 'Parallel Bars', 'Horizontal Bar'],
  'Ice Hockey': ['Center', 'Left Wing', 'Right Wing', 'Defenseman', 'Goalie'],
  'Lacrosse': ['Attack', 'Midfield', 'Defense', 'Goalie', 'Faceoff Specialist', 'Long Stick Midfielder (LSM)'],
  'Rowing': ['Sculler', 'Sweep Rower', 'Coxswain', 'Bow Seat', 'Stroke Seat'],
  'Rugby': ['Prop', 'Hooker', 'Lock', 'Flanker', 'Number Eight', 'Scrum-half', 'Fly-half', 'Center', 'Wing', 'Fullback'],
  'Ski': ['Alpine', 'Freestyle', 'Cross-Country', 'Ski Jumping', 'Nordic Combined', 'Snowboard'],
  'Soccer': ['Goalkeeper', 'Right Back', 'Left Back', 'Center Back', 'Defensive Midfielder', 'Central Midfielder', 'Attacking Midfielder', 'Right Wing', 'Left Wing', 'Striker', 'Forward'],
  'Softball': ['Pitcher', 'Catcher', 'First Base', 'Second Base', 'Third Base', 'Shortstop', 'Left Field', 'Center Field', 'Right Field', 'Designated Player', 'Flex Player', 'Utility Player'],
  'Swimming': ['Freestyle', 'Backstroke', 'Breaststroke', 'Butterfly', 'Individual Medley', 'Freestyle Relay', 'Medley Relay', 'Distance Freestyle', 'Sprint Freestyle'],
  'Tennis': ['Singles', 'Doubles', 'Mixed Doubles'],
  'Track and Field': ['Sprinter', 'Middle Distance', 'Long Distance', 'Hurdler', 'High Jump', 'Long Jump', 'Triple Jump', 'Pole Vault', 'Shot Put', 'Discus', 'Javelin', 'Hammer Throw', 'Decathlon/Heptathlon'],
  'Volleyball': ['Outside Hitter', 'Opposite Hitter', 'Setter', 'Middle Blocker', 'Libero', 'Defensive Specialist'],
  'Water Polo': ['Goalkeeper', 'Center Forward', 'Center Back', 'Driver', 'Winger', 'Utility Player'],
  'Wrestling': ['Lightweight', 'Welterweight', 'Middleweight', 'Heavyweight', 'Greco-Roman', 'Freestyle', 'Folkstyle']
};

// Get sorted list of sports
const SPORTS_LIST = Object.keys(SPORTS_POSITIONS_DATA).sort();
SPORTS_LIST.push('Other');

function NewProfile() {
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState(1);
  const [invitationData, setInvitationData] = useState(null);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [profile, setProfile] = useState({
    name: '',
    role: '', 
    birthYear: '',
    gender: '',
    sport: '',
    sportPosition: '',
    yearsPlaying: '',    
    email: '',
    password: '',
    confirmPassword: '',
    agreedToTerms: false,
    // Coach-specific fields
    teamSport: '',
    teamAgeGroup: '',
    teamSize: ''
  });
  const [positions, setPositions] = useState([]);

  useEffect(() => {
    // Track page view
    Mixpanel.track('View New Profile Page');
    
    logger.debug('🔍 NewProfile: Component mounted', {
      currentPath: location.pathname,
      queryParams: location.search,
      referrer: document.referrer || 'none',
      locationState: location.state
    });

    // Check for invitation code in URL or location state
    const params = new URLSearchParams(location.search);
    const codeFromQuery = params.get('code');
    const invitationFromQuery = params.get('invitation');
    const codeFromState = location.state?.invitationCode;
    
    logger.debug('🔍 NewProfile: Checking for invitation codes', { 
      codeFromQuery, 
      invitationFromQuery,
      codeFromState,
      hasLocationState: !!location.state
    });
    
    // Use the first available code (prioritize query params over state)
    const code = codeFromQuery || invitationFromQuery || codeFromState;
    
    if (code) {
      logger.debug('🔍 NewProfile: Invitation code found', { code, source: codeFromQuery ? 'code-query' : invitationFromQuery ? 'invitation-query' : 'state' });
      setInvitationData({ code });
      
      // If we have more data in state, use that too
      if (location.state?.initialRole || location.state?.initialSport || location.state?.initialName || location.state?.initialEmail) {
        setInvitationData(prev => ({
          ...prev,
          role: location.state.initialRole,
          sport: location.state.initialSport,
          name: location.state.initialName,
          email: location.state.initialEmail
        }));
        
        // Pre-fill profile data from invitation
        setProfile(prev => ({
          ...prev,
          name: location.state.initialName || prev.name,
          email: location.state.initialEmail || prev.email,
          sport: location.state.initialSport || prev.sport,
          role: location.state.initialRole ? 
            location.state.initialRole.charAt(0).toUpperCase() + location.state.initialRole.slice(1).toLowerCase() : 
            prev.role
        }));
      }
    }
  }, [location]);

  // Handle verified invitation
  const handleInvitationVerified = (invitation) => {
    logger.info('NewProfile: handleInvitationVerified received:', invitation); // Log received object
    if (invitation && invitation.code) {
      setInvitationData(invitation);
      
      // Pre-fill email if provided in the invitation
      if (invitation.email) {
        setProfile(prev => ({
          ...prev,
          email: invitation.email,
          role: invitation.role?.toLowerCase() || 'athlete'
        }));
      }
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'agreedToTerms') {
      setProfile(prevProfile => ({
        ...prevProfile,
        agreedToTerms: checked
      }));
      return;
    }
    
    setProfile(prevProfile => ({
      ...prevProfile,
      [name]: value
    }));
  };

  const handleSportChange = (e) => {
    const sportValue = e.target.value;
    setProfile({
      ...profile,
      sport: sportValue,
      sportPosition: '' 
    });
  };

  const handlePositionChange = (position) => {
    setProfile({
      ...profile,
      sportPosition: position
    });
  };

  const nextStep = () => setStep(prevStep => prevStep + 1);
  const prevStep = () => setStep(prevStep => prevStep - 1);

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    
    // Validate passwords match
    if (profile.password !== profile.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    // Validate required fields based on role
    if (!profile.name || !profile.email || !profile.password || !profile.confirmPassword ||
        !profile.birthYear || !profile.gender || !profile.role) {
      setError('Please fill all required fields');
      return;
    }
    
    // Role-specific validation
    if (profile.role === 'Coach') {
      if (!profile.teamSport || !profile.teamAgeGroup || !profile.teamSize) {
        setError('Please fill all team information fields');
        return;
      }
    } else {
      // Athlete or Parent validation
      if (!profile.sport || !profile.yearsPlaying) {
        setError('Please fill all sport information fields');
        return;
      }
    }
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(profile.email)) {
      setError('Please enter a valid email address');
      return;
    }
    
    // Validate password strength
    if (profile.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }
    
    // Validate terms agreement
    if (!profile.agreedToTerms) {
      setError('You must agree to the Terms & Conditions and Privacy Policy');
      return;
    }
    
    // Create profile and redirect to dashboard
    await createProfileAndRedirect();
  };

  // Helper function to create profile and redirect to dashboard
  const createProfileAndRedirect = async () => {
    logger.info('NewProfile: createProfileAndRedirect, current invitationData:', invitationData);
    logger.info('NewProfile: createProfileAndRedirect, invitationData.code:', invitationData?.code);

    setError('');
    setIsSubmitting(true);
    
    try {
      logger.debug('🔍 NewProfile: Creating profile with data', {
        name: profile.name,
        email: profile.email,
        hasPassword: !!profile.password,
        sport: profile.sport,
        position: profile.position,
        birthYear: profile.birthYear,
        gender: profile.gender
      });
      
      // Prepare registration data
      const registrationData = {
        name: profile.name,
        email: profile.email,
        password: profile.password,
        role: (profile.role || 'athlete').toLowerCase(),
        birthYear: profile.birthYear,
        gender: profile.gender,
        sport: profile.sport,
        sportPosition: profile.sportPosition,
        yearsPlaying: profile.yearsPlaying,
        goals: profile.goals,
        invitationCode: invitationData?.code || null
      };

      // Create the user account
      const startTime = Date.now();
      const response = await axios.post(`${API_URL}/api/users/register`, registrationData);
      
      logger.debug('🔍 NewProfile: Registration response received', {
        status: response.status,
        hasData: !!response.data,
        dataKeys: Object.keys(response.data || {})
      });
      
      if (response.data && response.data.token) {
        logger.debug('🔍 NewProfile: Registration successful, received token');
        
        // Store the token and user ID in localStorage - this is what PrivateRoute checks
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('userId', response.data.userId);
        
        // Set current time as last login time
        localStorage.setItem('lastLoginTime', new Date().getTime());
        
        // Track successful registration
        try {
          Mixpanel.track('Registration Success', {
            feature: 'Authentication',
            action: 'Registration Success',
            method: 'Email',
            role: profile.role || 'athlete',
            sport: profile.sport || 'Not specified'
          });
          
          // Identify user in Mixpanel - wrap in try/catch to handle potential errors
          Mixpanel.identify(response.data.userId);
          
          // Check if Mixpanel people object exists before calling set
          if (Mixpanel.people && typeof Mixpanel.people.set === 'function') {
            Mixpanel.people.set({
              $name: profile.name,
              $email: profile.email,
              role: profile.role || 'athlete',
              sport: profile.sport || 'Not specified',
              created_at: new Date().toISOString()
            });
          } else {
            logger.warn('🔍 NewProfile: Mixpanel people object not available');
          }
        } catch (mixpanelError) {
          // Log Mixpanel error but continue with registration flow
          logger.error('🔍 NewProfile: Mixpanel error', {
            error: mixpanelError.message,
            stack: mixpanelError.stack
          });
        }
        
        logger.debug('🔍 NewProfile: Registration complete, forcing page reload to onboarding');
        
        // Force a page reload to ensure the UserContext picks up the new token
        // This is more reliable than using navigate()
        // Redirect coaches to coach dashboard, other users to onboarding
        if (profile.role === 'Coach') {
          // Skip onboarding for coaches and redirect to coach dashboard
          window.location.href = '/coach-dashboard';
        } else {
          // Redirect to onboarding for non-coach users
          window.location.href = '/onboarding';
        }
      } else {
        logger.error('🔍 NewProfile: Invalid registration response', response.data);
        setError('Registration failed. Please try again.');
        setIsSubmitting(false);
      }
    } catch (error) {
      logger.debug('🔍 NewProfile: Registration error details', {
        message: error.message,
        name: error.name,
        stack: error.stack,
        response: error.response ? {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data
        } : 'No response',
        request: error.request ? 'Request was made but no response received' : 'Request setup failed'
      });
      
      // Handle specific error cases
      if (error.response) {
        if (error.response.status === 409) {
          setError('This email is already registered. Please use a different email or login.');
        } else if (error.response.data && error.response.data.message) {
          setError(error.response.data.message);
        } else {
          setError(`Registration failed: ${error.response.status} ${error.response.statusText}`);
        }
      } else if (error.request) {
        setError('No response received from server. Please check your internet connection and try again.');
      } else {
        setError(`Registration error: ${error.message}`);
      }
      
      // Track failed registration
      Mixpanel.track('Registration Failed', {
        feature: 'Authentication',
        action: 'Registration Failed',
        error: error.message || 'Unknown error'
      });
      
      setIsSubmitting(false);
    }
  };

  const getSteps = () => {
    if (profile.role === 'Coach') {
      return ['Name', 'Role', 'About You', 'Team Info', 'Account'];
    }
    return ['Name', 'Role', 'About You', 'Sport', 'Account'];
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <>
            <h3 className="text-xl font-bold mb-4">What's your name?</h3>
            <div className="mb-4">
              {/* Show read-only name field if from invitation */}
              {invitationData && invitationData.name ? (
                <>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Name (from coach invitation)
                  </label>
                  <div className="w-full py-2 px-3 border border-gray-300 rounded-md bg-gray-50 text-gray-700">
                    {invitationData.name}
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    Your name has been pre-filled by your coach
                  </p>
                </>
              ) : (
                <input 
                  type="text" 
                  id="name" 
                  name="name" 
                  value={profile.name} 
                  onChange={handleChange} 
                  className="w-full p-2 border rounded" 
                  placeholder="Enter your name" 
                  required 
                />
              )}
            </div>
          </>
        );
      case 2:
        return (
          <>
            <h3 className="text-xl font-bold mb-4">What is your role?</h3>
            <div className="mb-4">
              <div className="grid grid-cols-1 gap-3">
                {['Athlete', 'Coach'].map(role => (
                  <div 
                    key={role} 
                    className={`p-4 border rounded cursor-pointer ${
                      profile.role === role ? 'bg-blue-100 border-blue-500' : 'hover:bg-gray-50'
                    }`}
                    onClick={() => setProfile({...profile, role})}
                  >
                    <div className="flex items-center">
                      <input
                        type="radio"
                        id={`role-${role}`}
                        name="role"
                        value={role}
                        checked={profile.role === role}
                        onChange={() => setProfile({...profile, role})}
                        className="mr-2"
                        style={{
                          width: '18px',
                          height: '18px',
                          accentColor: '#3B82F6',
                          borderRadius: '50%',
                          border: '1.5px solid #d1d5db'
                        }}
                        required
                      />
                      <label htmlFor={`role-${role}`} className="text-lg font-medium cursor-pointer">{role}</label>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        );
      case 3:
        return (
          <>
            <h3 className="text-xl font-bold mb-4">Tell us about yourself</h3>
            <div className="mb-4">
              <label className="block mb-2">Birth Year</label>
              <select 
                id="birthYear" 
                name="birthYear" 
                value={profile.birthYear} 
                onChange={handleChange} 
                className="w-full p-2 border rounded" 
                required
              >
                <option value="">Select Birth Year</option>
                {Array.from({length: 2016 - 1950 + 1}, (_, i) => 2016 - i)
  .map(year => (
    <option key={year} value={year}>{year}</option>
  ))}
              </select>
            </div>
            <div className="mb-4">
              <label className="block mb-2">Gender</label>
              <select 
                id="gender" 
                name="gender" 
                value={profile.gender} 
                onChange={handleChange} 
                className="w-full p-2 border rounded" 
                required
              >
                <option value="">Select Gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="prefer-not-to-say">Prefer not to say</option>
              </select>
            </div>
          </>
        );
      case 4:
        if (profile.role === 'Coach') {
          return (
            <>
              <h3 className="text-xl font-bold mb-4">What is your team information?</h3>
              <div className="mb-4">
                <label className="block mb-2">Team Sport</label>
                <select 
                  id="teamSport" 
                  name="teamSport" 
                  value={profile.teamSport} 
                  onChange={handleChange} 
                  className="w-full p-2 border rounded" 
                  required
                >
                  <option value="">Select Team Sport</option>
                  {SPORTS_LIST.map(sport => (
                    <option key={sport} value={sport}>{sport}</option>
                  ))}
                </select>
              </div>
              <div className="mb-4">
                <label className="block mb-2">Team Age Group</label>
                <select 
                  id="teamAgeGroup" 
                  name="teamAgeGroup" 
                  value={profile.teamAgeGroup} 
                  onChange={handleChange} 
                  className="w-full p-2 border rounded" 
                  required
                >
                  <option value="">Select Team Age Group</option>
                  <option value="Youth">Youth (Under 18)</option>
                  <option value="High School">High School</option>
                  <option value="College">College</option>
                  <option value="Professional">Professional</option>
                </select>
              </div>
              <div className="mb-4">
                <label className="block mb-2">Team Size</label>
                <select 
                  id="teamSize" 
                  name="teamSize" 
                  value={profile.teamSize} 
                  onChange={handleChange} 
                  className="w-full p-2 border rounded" 
                  required
                >
                  <option value="">Select Team Size</option>
                  <option value="Small (1-10)">Small (1-10)</option>
                  <option value="Medium (11-20)">Medium (11-20)</option>
                  <option value="Large (21-50)">Large (21-50)</option>
                  <option value="Extra Large (51+)">Extra Large (51+)</option>
                </select>
              </div>
            </>
          );
        } else {
          return (
            <>
              <h3 className="text-xl font-bold mb-4">What is your primary sport?</h3>
              
              {/* Show read-only sport field if from invitation */}
              {invitationData && invitationData.sport ? (
                <div className="mb-4">
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Sport (from coach invitation)
                  </label>
                  <div className="w-full py-2 px-3 border border-gray-300 rounded-md bg-gray-50 text-gray-700">
                    {invitationData.sport}
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    Your sport has been pre-selected by your coach
                  </p>
                </div>
              ) : (
                <div className="mb-4">
                  <select
                    name="sport"
                    value={profile.sport}
                    onChange={handleSportChange}
                    className="w-full py-2 px-3 border border-neutral-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    required
                  >
                    <option value="">Select your primary sport</option>
                    {SPORTS_LIST.map(sport => (
                      <option key={sport} value={sport}>
                        {sport}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              
              {/* Show position selection for selected sport */}
              {profile.sport && SPORTS_POSITIONS_DATA[profile.sport] && (
                  <div className="mb-4">
                    <label className="block mb-2">{profile.sport} Position/Specialty</label>
                    <select
                      value={profile.sportPosition}
                      onChange={(e) => handlePositionChange(e.target.value)}
                      className="w-full p-2 border rounded"
                    >
                      <option value="">Select Position (Optional)</option>
                      {SPORTS_POSITIONS_DATA[profile.sport].map(position => (
                        <option key={`${profile.sport}-${position}`} value={position}>{position}</option>
                      ))}
                    </select>
                  </div>
                )}
              
              {/* Years Playing field */}
              {profile.sport && (
                  <div className="mb-4">
                    <label className="block mb-2">Years Playing {profile.sport}</label>
                    <select
                      name="yearsPlaying"
                      value={profile.yearsPlaying}
                      onChange={handleChange}
                      className="w-full p-2 border rounded"
                      required
                    >
                      <option value="">Select Years of Experience</option>
                      {(() => {
                        const currentYear = 2025; // Use the provided current local time
                        let maxExperience = 0;
                        let options = [];
                        if (profile.birthYear && !isNaN(Number(profile.birthYear))) {
                          const age = currentYear - Number(profile.birthYear);
                          if (age >= 5) {
                            maxExperience = age - 5;
                            for (let i = 0; i <= maxExperience; i++) {
                              options.push(
                                <option key={i} value={i.toString()}>{i === 0 ? '0 (Just starting)' : `${i} ${i === 1 ? 'year' : 'years'}`}</option>
                              );
                            }
                          } else {
                            options = [<option key={0} value="0">0 (Just starting)</option>];
                          }
                        }
                        return options;
                      })()}
                    </select>
                  </div>
                )}
            </>
          );
        }
      case 5:
        return (
          <>
            <h3 className="text-xl font-bold mb-4">Create your account</h3>
            <div className="mb-4">
              {/* Show read-only email field if from invitation */}
              {invitationData && invitationData.email ? (
                <>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Email (from coach invitation)
                  </label>
                  <div className="w-full py-2 px-3 border border-gray-300 rounded-md bg-gray-50 text-gray-700">
                    {invitationData.email}
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    Your email has been pre-filled by your coach
                  </p>
                </>
              ) : (
                <input 
                  type="email" 
                  id="email" 
                  name="email" 
                  value={profile.email} 
                  onChange={handleChange} 
                  className="w-full p-2 border rounded" 
                  placeholder="Enter your email" 
                  required 
                />
              )}
            </div>
            <div className="mb-4">
              <input 
                type="password" 
                id="password" 
                name="password" 
                value={profile.password} 
                onChange={handleChange} 
                className="w-full p-2 border rounded" 
                placeholder="Create a password" 
                required 
              />
            </div>
            <div className="mb-4">
              <input 
                type="password" 
                id="confirmPassword" 
                name="confirmPassword" 
                value={profile.confirmPassword} 
                onChange={handleChange} 
                className={`w-full p-2 border rounded ${
                  profile.confirmPassword && profile.password !== profile.confirmPassword 
                    ? 'border-red-500' 
                    : ''
                }`} 
                placeholder="Confirm password" 
                required 
              />
              {profile.confirmPassword && profile.password !== profile.confirmPassword && (
                <p className="text-red-500 text-sm mt-1">Passwords do not match</p>
              )}
            </div>
            <div className="mb-4 mt-6">
              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="agreedToTerms"
                  name="agreedToTerms"
                  checked={profile.agreedToTerms}
                  onChange={handleChange}
                  className="auth-checkbox mr-2 aspect-square"
                  style={{ width: '1rem', height: '1rem' }}
                  required
                />
                <label htmlFor="agreedToTerms" className="text-sm">
                  I agree to the <Link to="/terms-and-conditions" className="text-blue-500 hover:text-blue-700" target="_blank" rel="noopener noreferrer">Terms & Conditions</Link> and <Link to="/privacy-policy" className="text-blue-500 hover:text-blue-700" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>
                </label>
              </div>
            </div>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-base-200 p-4 py-8">
      <div className="w-full max-w-md bg-transparent">
        <div className="px-6 py-8 sm:px-8">
          {step === 1 && (
            <div className="flex flex-col items-center mb-6">
              <img
                src="/images/mental-edge-coach-logo.png"
                alt="Mental Edge Coach"
                className="w-26 mb-2"
                onError={e => { e.target.onerror = null; e.target.style.display = 'none'; }}
              />
              <h2 className="text-2xl font-bold text-primary">Create Profile</h2>
            </div>
          )}
          
          {/* Invitation Handler */}
          <InvitationHandler 
            onInvitationVerified={handleInvitationVerified}
            showUI={step === 1}
          />

          {/* Only show the form if no invitation code is required or if it's been verified */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {renderStep()}

            {error && (
              <div className="text-center mb-4">
                <p className="text-error font-medium">{error}</p>
              </div>
            )}

            <div className="flex justify-between mt-4">
              {step > 1 && (
                <button
                  type="button"
                  onClick={prevStep}
                  className="btn btn-secondary"
                  disabled={isSubmitting}
                >
                  Previous
                </button>
              )}
              <button
                type="button"
                onClick={step === 5 ? handleSubmit : nextStep}
                className="btn btn-primary"
                disabled={isSubmitting}
                style={{ marginLeft: step > 1 ? '0.5rem' : 'auto' }}
              >
                {isSubmitting ? (
                  <>
                    <span className="loading loading-spinner loading-xs mr-2"></span>
                    {step < 5 ? 'Processing...' : 'Creating Profile'}
                  </>
                ) : (
                  step < 5 ? 'Next' : 'Create Profile'
                )}
              </button>
            </div>
          </form>

          <div className="text-center mt-6">
            <span className="text-sm text-base-content">
              Already have an account?{' '}
              <Link to="/login" className="text-primary font-bold underline">Sign in</Link>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default NewProfile;
